import I18n from 'I18n';
import { getObjectTypeNamePlural } from 'marketing-platform-lib/utils/objectName';
import { OBJECT_TYPE } from 'framework-asset-preview-lib/internal/shared/constants/objectTypes';
import allSettled from 'framework-asset-preview-lib/internal/shared/utils/allSettled';
import { getPropertyValue } from 'framework-asset-preview-lib/internal/shared/utils/crmObject';
import { getFormsUsedAsCrmObjects } from 'framework-asset-preview-lib/internal/objectTypes/0-10/api';
import { fetchAssociatedWorkflowsAsCrmObjects, fetchAssociatedListsAsCrmObjects, fetchAssociatedContactsAsCrmObjects } from 'framework-asset-preview-lib/internal/objectTypes/0-29/api';
import { fetchSmsAssociatedListsAsCrmObjects, fetchSmsWorkflowAssociationGroup } from 'framework-asset-preview-lib/internal/objectTypes/0-124/api';
import { fetchFormAssociatedPagesAsCrmObjects, fetchFormAssociatedWorkflowsAsCrmObject } from 'framework-asset-preview-lib/internal/objectTypes/0-28/api';
import { fetchTrackingUrlsAsCrmSearchResponse } from 'framework-asset-preview-lib/internal/objectTypes/TRACKING_URL/api';
import { fetchWhatsAppWorkflowUsages } from 'framework-asset-preview-lib/internal/objectTypes/0-830/apis/workflows';
import { createAssociationGroup } from 'framework-asset-preview-lib/internal/associations/utils/associations';
import cachedHttp from 'framework-asset-preview-lib/internal/shared/api/cachedHttp';
import { SMS_TYPE } from 'messaging-types-lib/content/contentSms';
import { TRACKING_URL_PAGE_SIZE } from 'framework-asset-preview-lib/utils/constants';
export function fetchAssociatedObjects({
  associationDefinitions,
  objectId,
  offset
}) {
  const associationPromises = associationDefinitions.map(association => cachedHttp.get(`associations/v1/associations/${association.fromObjectTypeId}/${objectId}/associations-to/${association.toObjectTypeId}?limit=10&offset=${offset}`));
  return Promise.all(associationPromises).then(responses => {
    return responses.reduce((acc, response) => {
      acc.hasMore = acc.hasMore || response.hasMore;
      acc.offset = acc.offset || response.offset;
      acc.results = [...acc.results, ...response.results.map(result => result.objectId)];
      return acc;
    }, {
      results: [],
      hasMore: false,
      offset: 0
    });
  }).catch(() => {
    return {
      hasMore: false,
      results: [],
      offset
    };
  });
}

/**
 * 1 - Fetch association definitions
 * 2 - Set @fromCrmObjectTypeDefinition and @toCrmObjectTypeDefinition in each association
 * 3 - Sort results alphabetically
 */
export function fetchAssociationsFromCRM(objectType, crmObjectTypeDefinitionsForPortal, crmAssociations) {
  if (crmAssociations.length === 0) {
    return Promise.resolve([]);
  }
  return cachedHttp.get(`associations/v1/definitions/${objectType}`).then(associationDefinitions => {
    /**
     * Group association definitions and
     * set fromCrmObjectTypeDefinition and toCrmObjectTypeDefinition props
     * for sorting
     */
    const associationGroup = associationDefinitions.filter(association => crmAssociations.includes(association.name)).reduce((acc, associationDefinition) => {
      const currentAssociationGroup = acc[associationDefinition.toObjectTypeId] || [];
      const fromCrmObjectTypeDefinition = crmObjectTypeDefinitionsForPortal.find(object => object.objectTypeId === associationDefinition.fromObjectTypeId);
      const toCrmObjectTypeDefinition = crmObjectTypeDefinitionsForPortal.find(object => object.objectTypeId === associationDefinition.toObjectTypeId);
      if (!toCrmObjectTypeDefinition || !fromCrmObjectTypeDefinition) {
        return acc;
      }
      return Object.assign({}, acc, {
        [associationDefinition.toObjectTypeId]: [...currentAssociationGroup, Object.assign({}, associationDefinition, {
          fromCrmObjectTypeDefinition,
          toCrmObjectTypeDefinition
        })]
      });
    }, {});
    return Object.entries(associationGroup).map(([__, value]) => {
      const {
        toCrmObjectTypeDefinition,
        toObjectTypeId,
        fromCrmObjectTypeDefinition,
        fromObjectTypeId
      } = value[0];
      return {
        associationDefinitions: value,
        crmDefined: true,
        fromCrmObjectTypeDefinition,
        fromObjectTypeId,
        objectTypeName: getObjectTypeNamePlural({
          objectTypeId: toCrmObjectTypeDefinition.objectTypeId,
          typeDefinition: toCrmObjectTypeDefinition,
          textTransform: 'capitalize'
        }),
        toObjectTypeId,
        toCrmObjectTypeDefinition
      };
    });
  });
}
export function fetchAssociationsFromOtherSources({
  crmObject,
  crmObjectTypeDefinitionsForPortal,
  nonCrmObject
}) {
  switch (crmObject.objectTypeId) {
    default:
      return Promise.resolve([]);
    case OBJECT_TYPE.CAMPAIGN:
      {
        const fromCrmObjectTypeDefinition = crmObjectTypeDefinitionsForPortal.find(definition => definition.objectTypeId === crmObject.objectTypeId);
        if (!fromCrmObjectTypeDefinition) {
          return Promise.resolve([]);
        }
        return Promise.allSettled([fetchTrackingUrlsAsCrmSearchResponse(getPropertyValue(crmObject, 'hs_origin_asset_id'), 0, TRACKING_URL_PAGE_SIZE)]).then(([trackingUrlsResponse]) => {
          if (trackingUrlsResponse.status === 'fulfilled') {
            return [{
              associatedObjects: trackingUrlsResponse.value.results,
              associationDefinitions: [],
              crmDefined: false,
              fromCrmObjectTypeDefinition,
              fromObjectTypeId: crmObject.objectTypeId,
              objectTypeName: I18n.text('frameworkAssetPreviewLib.propertyLabel.TRACKING_URL.namePlural'),
              toCrmObjectTypeDefinition: {
                name: OBJECT_TYPE.TRACKING_URL,
                primaryDisplayLabelPropertyName: 'hs_name'
              },
              toObjectTypeId: OBJECT_TYPE.TRACKING_URL,
              hasMore: trackingUrlsResponse.value.hasMore,
              total: trackingUrlsResponse.value.total,
              offset: trackingUrlsResponse.value.offset
            }];
          }
          return Promise.resolve([]);
        });
      }
    case OBJECT_TYPE.FORM:
      return allSettled([fetchFormAssociatedWorkflowsAsCrmObject(crmObject), fetchFormAssociatedPagesAsCrmObjects(crmObject)]).then(result => {
        const [associatedWorkflowsResponse, contentResponse] = result;
        let contentAssociationGroups = [];
        let workflowAssociationGroup = undefined;
        if (associatedWorkflowsResponse.status === 'fulfilled') {
          workflowAssociationGroup = createAssociationGroup({
            crmObjects: associatedWorkflowsResponse.value,
            crmObjectTypeDefinitionsForPortal,
            fromObjectTypeId: crmObject.objectTypeId,
            toObjectTypeId: OBJECT_TYPE.WORKFLOW
          });
        }
        if (contentResponse.status === 'fulfilled') {
          contentAssociationGroups = Object.keys(contentResponse.value).map(key => createAssociationGroup({
            crmObjects: contentResponse.value[key],
            crmObjectTypeDefinitionsForPortal,
            fromObjectTypeId: crmObject.objectTypeId,
            toObjectTypeId: key
          }));
        }
        return [workflowAssociationGroup, ...contentAssociationGroups].filter(Boolean);
      });
    case OBJECT_TYPE.MARKETING_EMAIL:
      return allSettled([fetchAssociatedWorkflowsAsCrmObjects(crmObject), fetchAssociatedListsAsCrmObjects({
        nonCrmObject,
        isIncluded: true
      }), fetchAssociatedListsAsCrmObjects({
        nonCrmObject,
        isIncluded: false
      }), fetchAssociatedContactsAsCrmObjects({
        nonCrmObject,
        isIncluded: true
      }), fetchAssociatedContactsAsCrmObjects({
        nonCrmObject,
        isIncluded: false
      })]).then(result => {
        const [associatedWorkflowsResponse, includedListsResponse, excludedListsResponse, vidsIncludedResponse, vidsExcludedResponse] = result;
        let includedListAssociationGroup = undefined;
        let excludedListAssociationGroup = undefined;
        let workflowAssociationGroup = undefined;
        let journeyAssociationGroup = undefined;
        let vidsIncludedAssociationGroup = undefined;
        let vidsExcludedAssociationGroup = undefined;
        if (associatedWorkflowsResponse.status === 'fulfilled') {
          const {
            workflows,
            journeyStages
          } = associatedWorkflowsResponse.value.reduce((acc, workflow) => {
            var _workflow$properties;
            if ((workflow === null || workflow === void 0 || (_workflow$properties = workflow.properties) === null || _workflow$properties === void 0 || (_workflow$properties = _workflow$properties.hs_source_app) === null || _workflow$properties === void 0 ? void 0 : _workflow$properties.value) === 'JOURNEY_STAGE') {
              acc.journeyStages.push(workflow);
            } else {
              acc.workflows.push(workflow);
            }
            return acc;
          }, {
            workflows: [],
            journeyStages: []
          });
          workflowAssociationGroup = createAssociationGroup({
            crmObjects: workflows,
            crmObjectTypeDefinitionsForPortal,
            fromObjectTypeId: crmObject.objectTypeId,
            toObjectTypeId: OBJECT_TYPE.WORKFLOW
          });
          journeyAssociationGroup = journeyStages.length ? createAssociationGroup({
            crmObjects: journeyStages,
            crmObjectTypeDefinitionsForPortal,
            fromObjectTypeId: crmObject.objectTypeId,
            toObjectTypeId: OBJECT_TYPE.AUTOMATION_JOURNEY
          }) : undefined;
        }
        const fromCrmObjectTypeDefinition = crmObjectTypeDefinitionsForPortal.find(definition => definition.objectTypeId === crmObject.objectTypeId);
        const toListDefinition = crmObjectTypeDefinitionsForPortal.find(definition => definition.objectTypeId === OBJECT_TYPE.OBJECT_LIST);
        if (includedListsResponse.status === 'fulfilled') {
          includedListAssociationGroup = !fromCrmObjectTypeDefinition || !toListDefinition ? undefined : {
            associationDefinitions: [],
            associatedObjects: includedListsResponse.value,
            crmDefined: false,
            fromCrmObjectTypeDefinition,
            fromObjectTypeId: crmObject.objectTypeId,
            toCrmObjectTypeDefinition: toListDefinition,
            toObjectTypeId: OBJECT_TYPE.OBJECT_LIST,
            objectTypeName: I18n.text('frameworkAssetPreviewLib.propertyLabel.INCLUDED_LISTS.namePlural')
          };
        }
        if (excludedListsResponse.status === 'fulfilled') {
          excludedListAssociationGroup = !fromCrmObjectTypeDefinition || !toListDefinition ? undefined : {
            associationDefinitions: [],
            associatedObjects: excludedListsResponse.value,
            crmDefined: false,
            fromCrmObjectTypeDefinition,
            fromObjectTypeId: crmObject.objectTypeId,
            toCrmObjectTypeDefinition: toListDefinition,
            toObjectTypeId: OBJECT_TYPE.OBJECT_LIST,
            objectTypeName: I18n.text('frameworkAssetPreviewLib.propertyLabel.EXCLUDED_LISTS.namePlural')
          };
        }
        if (vidsIncludedResponse.status === 'fulfilled') {
          vidsIncludedAssociationGroup = !fromCrmObjectTypeDefinition ? undefined : {
            associationDefinitions: [],
            associatedObjects: vidsIncludedResponse.value,
            crmDefined: false,
            fromCrmObjectTypeDefinition,
            fromObjectTypeId: crmObject.objectTypeId,
            toCrmObjectTypeDefinition: {
              name: OBJECT_TYPE.CONTACT,
              primaryDisplayLabelPropertyName: 'email'
            },
            toObjectTypeId: OBJECT_TYPE.CONTACT,
            objectTypeName: I18n.text('frameworkAssetPreviewLib.propertyLabel.INCLUDED_CONTACTS.namePlural')
          };
        }
        if (vidsExcludedResponse.status === 'fulfilled') {
          vidsExcludedAssociationGroup = !fromCrmObjectTypeDefinition ? undefined : {
            associationDefinitions: [],
            associatedObjects: vidsExcludedResponse.value,
            crmDefined: false,
            fromCrmObjectTypeDefinition,
            fromObjectTypeId: crmObject.objectTypeId,
            toCrmObjectTypeDefinition: {
              name: OBJECT_TYPE.CONTACT,
              primaryDisplayLabelPropertyName: 'email'
            },
            toObjectTypeId: OBJECT_TYPE.CONTACT,
            objectTypeName: I18n.text('frameworkAssetPreviewLib.propertyLabel.EXCLUDED_CONTACTS.namePlural')
          };
        }
        return [workflowAssociationGroup, includedListAssociationGroup, excludedListAssociationGroup, journeyAssociationGroup, vidsIncludedAssociationGroup, vidsExcludedAssociationGroup].filter(Boolean);
      });
    case OBJECT_TYPE.MARKETING_SMS:
      return allSettled([fetchSmsAssociatedListsAsCrmObjects({
        nonCrmObject,
        isIncluded: true
      }), fetchSmsAssociatedListsAsCrmObjects({
        nonCrmObject,
        isIncluded: false
      }), fetchSmsWorkflowAssociationGroup(crmObject)]).then(result => {
        const [includedListsResponse, excludedListsResponse, associatedWorkflowsResponse] = result;
        let includedListAssociationGroup = undefined;
        let excludedListAssociationGroup = undefined;
        let workflowAssociationGroup = undefined;
        const fromCrmObjectTypeDefinition = crmObjectTypeDefinitionsForPortal.find(definition => definition.objectTypeId === crmObject.objectTypeId);
        const toCrmObjectTypeDefinition = crmObjectTypeDefinitionsForPortal.find(definition => definition.objectTypeId === OBJECT_TYPE.OBJECT_LIST);
        if (includedListsResponse.status === 'fulfilled') {
          includedListAssociationGroup = !fromCrmObjectTypeDefinition || !toCrmObjectTypeDefinition ? undefined : {
            associationDefinitions: [],
            associatedObjects: includedListsResponse.value,
            crmDefined: false,
            fromCrmObjectTypeDefinition,
            fromObjectTypeId: crmObject.objectTypeId,
            toCrmObjectTypeDefinition,
            toObjectTypeId: OBJECT_TYPE.OBJECT_LIST,
            objectTypeName: I18n.text('frameworkAssetPreviewLib.propertyLabel.INCLUDED_LISTS.namePlural')
          };
        }
        if (excludedListsResponse.status === 'fulfilled') {
          excludedListAssociationGroup = !fromCrmObjectTypeDefinition || !toCrmObjectTypeDefinition ? undefined : {
            associationDefinitions: [],
            associatedObjects: excludedListsResponse.value,
            crmDefined: false,
            fromCrmObjectTypeDefinition,
            fromObjectTypeId: crmObject.objectTypeId,
            toCrmObjectTypeDefinition,
            toObjectTypeId: OBJECT_TYPE.OBJECT_LIST,
            objectTypeName: I18n.text('frameworkAssetPreviewLib.propertyLabel.EXCLUDED_LISTS.namePlural')
          };
        }
        const isAutomatedSMS = getPropertyValue(crmObject, 'hs_sms_type') === SMS_TYPE.AUTOMATED;
        if (associatedWorkflowsResponse.status === 'fulfilled' && isAutomatedSMS) {
          workflowAssociationGroup = createAssociationGroup({
            crmObjects: associatedWorkflowsResponse.value['@ok'],
            crmObjectTypeDefinitionsForPortal,
            fromObjectTypeId: crmObject.objectTypeId,
            toObjectTypeId: OBJECT_TYPE.WORKFLOW
          });
        }
        return [workflowAssociationGroup, includedListAssociationGroup, excludedListAssociationGroup].filter(Boolean);
      });
    case OBJECT_TYPE.WHATSAPP:
      {
        const sendingMethod = getPropertyValue(crmObject, 'hs_sending_method');
        if (sendingMethod !== 'AUTOMATION') {
          return Promise.resolve([]);
        }
        const contentId = getPropertyValue(crmObject, 'hs_content_id');
        if (!contentId) {
          return Promise.resolve([]);
        }
        const whatsAppId = Number(contentId);
        return allSettled([fetchWhatsAppWorkflowUsages(whatsAppId)]).then(result => {
          const [associatedWorkflowsResponse] = result;
          let workflowAssociationGroup = undefined;
          if (associatedWorkflowsResponse.status === 'fulfilled') {
            workflowAssociationGroup = createAssociationGroup({
              crmObjects: associatedWorkflowsResponse.value['@ok'],
              crmObjectTypeDefinitionsForPortal,
              fromObjectTypeId: crmObject.objectTypeId,
              toObjectTypeId: OBJECT_TYPE.WORKFLOW
            });
          }
          return [workflowAssociationGroup].filter(Boolean);
        });
      }
    case OBJECT_TYPE.BLOG_POST:
    case OBJECT_TYPE.CONTENT:
    case OBJECT_TYPE.LANDING_PAGE:
    case OBJECT_TYPE.SITE_PAGE:
      {
        const contentId = getPropertyValue(crmObject, 'hs_content_id');
        const intContentId = parseInt(contentId, 10);
        return allSettled([getFormsUsedAsCrmObjects(intContentId)]).then(result => {
          let formAssociationGroup = undefined;
          const [formResponse] = result;
          if (formResponse.status === 'fulfilled') {
            formAssociationGroup = createAssociationGroup({
              crmObjects: formResponse.value,
              crmObjectTypeDefinitionsForPortal,
              fromObjectTypeId: crmObject.objectTypeId,
              toObjectTypeId: OBJECT_TYPE.FORM
            });
          }
          return [formAssociationGroup].filter(Boolean);
        });
      }
  }
}